import type { ReactNode } from 'react';

/**
 * The sidebar-page shell: a sticky nav of entries and one panel beside it.
 *
 * It is the storybook's own shape (`.sb-shell` / `.sb-nav` / `.sb-nav-link`), extracted so the admin Tests
 * page can be the same page rather than a second implementation of it. The class names stay `.sb-*`: they are
 * what public/app.css styles and what the storybook's browser test addresses, and a rename would be churn with
 * no reader-visible gain.
 *
 * The address is the state — each entry is a real `<a href="#…">`, so a fresh load and back/forward land on the
 * right entry. Pages own what "selecting" means (the storybook swaps a panel inside the click; the tests page
 * scrolls its shell into view), hence `onSelect` rather than a navigate here.
 */
export type SideNavItem = { key: string; label: string; count?: number | string; title?: string };

export function SideNavShell({
  items,
  active,
  onSelect,
  hrefOf,
  ariaLabel,
  groupLabel,
  wrap = false,
  children,
}: {
  items: SideNavItem[];
  active: string;
  onSelect: (key: string) => void;
  hrefOf: (key: string) => string;
  ariaLabel: string;
  /** Omitted when the entries speak for themselves (a nav whose first entry is not one of the group). */
  groupLabel?: string;
  /**
   * On a phone the storybook's nav is a horizontal strip, which suits ten short entries. The tests page has
   * fourteen with counts, so it wraps to two or three rows instead of scrolling most of a screen sideways.
   */
  wrap?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="sb-shell">
      <nav className="sb-nav" aria-label={ariaLabel} data-wrap={wrap ? 'true' : undefined}>
        {groupLabel ? <div className="sb-nav-group">{groupLabel}</div> : null}
        {items.map((it) => (
          <a
            key={it.key}
            href={hrefOf(it.key)}
            className={'sb-nav-link' + (active === it.key ? ' active' : '')}
            aria-current={active === it.key ? 'true' : undefined}
            title={it.title}
            onClick={(e) => { e.preventDefault(); onSelect(it.key); }}
          >
            <span className="sb-nav-label">{it.label}</span>
            {it.count === undefined ? null : <span className="sb-nav-count">{it.count}</span>}
          </a>
        ))}
      </nav>

      <div className="sb-main">{children}</div>
    </div>
  );
}

/**
 * The entry an address names, or the first one — a page's `sectionFrom(hash)`.
 *
 * `prefix` is the id stem the page writes (`sb-` on the storybook, `area-` on the tests page). An unknown or
 * empty hash falls back to the first entry rather than to an empty panel: a link with a typo should still show
 * the page.
 */
export function activeFromHash(hash: string, keys: string[], prefix: string): string {
  const named = (hash || '').replace(/^#/, '');
  if (named.startsWith(prefix)) {
    const key = named.slice(prefix.length);
    if (keys.includes(key)) return key;
  }
  return keys[0] || '';
}
